<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NucleoWorkOrdersAPI from 'dashboard/api/nucleoWorkOrders';

const props = defineProps({
  request: { type: Object, required: true },
});

const { t } = useI18n();
const notify = useAlert;
const documents = ref([]);
const actions = ref([]);
const uploadingKind = ref('');
const hasContent = computed(
  () => documents.value.length > 0 || actions.value.length > 0
);
const kindLabels = {
  boleto: t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.KINDS.BOLETO'),
  comprovante: t(
    'NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.KINDS.COMPROVANTE'
  ),
};
const kindLabel = kind => kindLabels[kind] || kind;
const idempotencyKey = (kind, file) =>
  `nucleo-payment:${props.request.id}:${kind}:${file.name}:${file.size}:${file.lastModified}`;

const sync = () => {
  documents.value = [...(props.request.payment_documents || [])];
  actions.value = [...(props.request.payment_document_actions || [])];
};

const upload = async (kind, event) => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file || uploadingKind.value) return;

  uploadingKind.value = kind;
  try {
    const response = await NucleoWorkOrdersAPI.addPaymentDocument(
      props.request.imovel_listing_id,
      props.request.id,
      { kind, file, idempotency_key: idempotencyKey(kind, file) }
    );
    documents.value = response.data.data.payment_documents || [];
    actions.value = response.data.data.payment_document_actions || [];
    notify(t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.SUCCESS'));
  } catch (error) {
    notify(
      error?.response?.data?.message ||
        t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.ERROR')
    );
  } finally {
    uploadingKind.value = '';
  }
};

watch(
  () => [
    props.request.payment_documents,
    props.request.payment_document_actions,
  ],
  sync,
  { immediate: true, deep: true }
);
</script>

<template>
  <div>
    <section v-if="hasContent" class="grid gap-3" data-test="nucleo-payment">
      <div v-if="actions.length" class="flex flex-wrap gap-2">
        <label
          v-for="kind in actions"
          :key="kind"
          class="inline-flex cursor-pointer items-center gap-2 rounded-lais border border-transparent px-3 py-2 text-sm font-semibold text-lais-link hover:bg-lais-primary-50"
          :class="{ 'pointer-events-none opacity-60': uploadingKind }"
        >
          <span class="i-lucide-file-up size-4" aria-hidden="true" />
          {{
            uploadingKind === kind
              ? t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.UPLOADING')
              : t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.UPLOAD')
          }}
          <input
            class="sr-only"
            type="file"
            accept=".doc,.docx,.pdf"
            :disabled="Boolean(uploadingKind)"
            :data-test="`payment-upload-${kind}`"
            @change="upload(kind, $event)"
          />
        </label>
      </div>

      <div
        v-if="documents.length"
        class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
      >
        <h2
          class="border-b border-lais-border-soft px-5 py-4 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.TITLE') }}
        </h2>
        <div
          class="hidden grid-cols-[minmax(8rem,0.35fr)_minmax(0,1fr)] gap-4 bg-lais-bg-soft px-5 py-3 text-xs font-semibold uppercase text-lais-text-muted sm:grid"
        >
          <span>
            {{ t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.TYPE') }}
          </span>
          <span>
            {{ t('NUCLEO_WORK_ORDERS.DETAIL.PAYMENT_DOCUMENTS.FILENAME') }}
          </span>
        </div>
        <a
          v-for="document in documents"
          :key="document.id"
          :href="document.url"
          target="_blank"
          rel="noopener noreferrer"
          class="grid gap-1 border-t border-lais-border-soft px-5 py-3 text-sm first:border-t-0 hover:bg-lais-primary-50 sm:grid-cols-[minmax(8rem,0.35fr)_minmax(0,1fr)] sm:gap-4"
        >
          <span class="font-semibold text-lais-text">
            {{ kindLabel(document.kind) }}
          </span>
          <span class="truncate text-lais-link">{{ document.filename }}</span>
        </a>
      </div>
    </section>
  </div>
</template>
